import dayjs from "dayjs"
import { getUserAvatarUrl } from "./userAvatar"
import { resolveSessionCity, resolveSessionProject } from "./session"

export const MEMBERS_PAGE_SIZE = 10
export const SEARCH_DEBOUNCE_MS = 300
export const DEFAULT_SORT_BY = "joined_at"
export const DEFAULT_SORT_DIRECTION = "desc"

export const TIMELOG_REASONS = {
  start: "Session timer started",
  break: "Break",
  break_end: "Resumed from break",
  unload: "Left the application",
  logout: "Paused on logout",
  exit: "Paused before exiting session",
}

const TIMELOG_ACTION_PRIORITY = ["break", "break_end", "start"]

export const SESSION_COLLAB_STATUS_LABEL = {
  invited: "Invited",
  joined: "Joined",
  left: "Left",
  active: "Joined",
  pending: "Invited",
  paused: "Paused",
  exited: "Left",
}

/** Normalize API / UI status into invited | joined | left. */
export const normalizeSessionCollabStatus = status => {
  const key = String(status || "").toLowerCase().trim()
  if (key === "invited" || key === "pending") return "invited"
  if (key === "left" || key === "exited") return "left"
  if (
    key === "joined" ||
    key === "active" ||
    key === "paused" ||
    key === "in session" ||
    key === "in_session"
  ) {
    return "joined"
  }
  return key
}

export const matchesSessionCollabStatusFilters = (member, statuses = "") => {
  const list = Array.isArray(statuses)
    ? statuses
    : statuses
      ? [statuses]
      : []
  if (list.length === 0) return true
  const allowed = new Set(
    list.map(normalizeSessionCollabStatus).filter(Boolean),
  )
  if (allowed.size === 0) return true
  const key = normalizeSessionCollabStatus(
    member?.statusKey || member?.raw?.status || member?.status,
  )
  return allowed.has(key)
}

/**
 * GET /sessions/:id/timelog requires the current user to be part of the session.
 * When current_user_collaboration_status is null/empty, skip that call (403).
 */
export const canAccessSessionTimelog = collaborationStatus => {
  const status = String(collaborationStatus || "")
    .toLowerCase()
    .trim()
  return Boolean(status)
}

const PAUSED_STATES = new Set(["break", "on_break", "paused"])
const ACTIVE_STATES = new Set(["active", "working", "running", "started"])

export const capitalize = value => {
  if (!value || typeof value !== "string") return "—"
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/** API `visibility_type: global` → user-facing "Public". */
export const formatSessionVisibilityLabel = visibilityType => {
  const key = String(visibilityType || "").trim().toLowerCase()
  if (key === "private") return "Private"
  if (key === "global") return "Public"
  if (!key) return "—"
  return capitalize(key)
}

export const getSessionVisibilityClassKey = visibility => {
  const key = String(visibility || "").trim().toLowerCase()
  if (key === "private") return "private"
  if (key === "public" || key === "global") return "public"
  return key || "none"
}

export const formatSessionType = type => {
  if (!type) return "—"
  const label = capitalize(type)
  return /session/i.test(label) ? label : `${label} Session`
}

export const formatDateTime = value => {
  if (!value) return "—"
  const date = dayjs(value)
  return date.isValid() ? date.format("MMMM D, YYYY, h:mm A") : "—"
}

export const formatClockTime = value => {
  if (!value) return "—"
  const date = dayjs(value)
  return date.isValid() ? date.format("HH:mm:ss") : "—"
}

export const formatWorkedHours = value => {
  const hours = Number(value)
  if (!Number.isFinite(hours) || hours <= 0) return "0h 0m 0s"
  const totalSeconds = Math.round(hours * 3600)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${h}h ${m}m ${s}s`
}

export const hoursToElapsedSeconds = hours => {
  const value = Number(hours)
  if (!Number.isFinite(value) || value < 0) return 0
  return Math.round(value * 3600)
}

/** Big session timer: live timelog while running/break; otherwise total_working_hours. */
export const resolveSessionDisplayTimer = ({
  apiSession = {},
  timerState = {},
  sessionEnded = false,
  hasLeftSession = false,
  isSessionParticipant = false,
} = {}) => {
  const isLiveTimer =
    isSessionParticipant &&
    !hasLeftSession &&
    !sessionEnded &&
    timerState.timerStatus !== "ended" &&
    (timerState.isRunning || timerState.isOnBreak)

  if (isLiveTimer) {
    return {
      timer: timerState.timer || formatSeconds(timerState.elapsedSeconds || 0),
      elapsedSeconds: timerState.elapsedSeconds || 0,
    }
  }

  const totalHours = Number(
    apiSession.total_working_hours ?? apiSession.total_worked_hours,
  )
  if (Number.isFinite(totalHours) && totalHours >= 0) {
    const elapsedSeconds = hoursToElapsedSeconds(totalHours)
    return {
      timer: formatSeconds(elapsedSeconds),
      elapsedSeconds,
    }
  }

  if (sessionEnded && apiSession.started_at && apiSession.ended_at) {
    const elapsedSeconds = Math.max(
      0,
      dayjs(apiSession.ended_at).diff(dayjs(apiSession.started_at), "second"),
    )
    return {
      timer: formatElapsedTimer(apiSession.started_at, apiSession.ended_at),
      elapsedSeconds,
    }
  }

  return {
    timer: timerState.timer || formatSeconds(timerState.elapsedSeconds || 0),
    elapsedSeconds: timerState.elapsedSeconds || 0,
  }
}

const CLOCK_DURATION_PATTERN = /^(\d{1,}):(\d{2})(?::(\d{2}))?$/

export const formatClockDuration = value => {
  if (value == null || value === "") return "0h 0m 0s"
  const trimmed = String(value).trim()
  const match = trimmed.match(CLOCK_DURATION_PATTERN)
  if (match) {
    const h = Number(match[1]) || 0
    const m = Number(match[2]) || 0
    const s = Number(match[3] || 0) || 0
    return `${h}h ${m}m ${s}s`
  }
  return formatWorkedHours(value)
}

/** Normalize leave-session API payload into display time for the success modal.
 * Prefer the leaving user's hours only — never session total_working_hours.
 */
export const resolveLeaveSessionLoggedTime = (result = {}, session = null) => {
  const candidates = [
    result.current_user_working_hours,
    result.currentUserWorkingHours,
    session?.currentUserWorkingHours,
  ]

  for (const value of candidates) {
    if (value == null || value === "") continue
    if (
      typeof value === "string" &&
      CLOCK_DURATION_PATTERN.test(String(value).trim())
    ) {
      return formatClockDuration(value)
    }
    const num = Number(value)
    if (Number.isFinite(num) && num >= 0) {
      return formatWorkedHours(num)
    }
  }

  return "0h 0m 0s"
}

export const formatLoggedHours = (start, end) => {
  if (!start) return "—"
  const startDate = dayjs(start)
  const endDate = end ? dayjs(end) : null
  if (!startDate.isValid()) return "—"
  if (!endDate || !endDate.isValid()) return "—"
  const minutes = Math.max(0, endDate.diff(startDate, "minute"))
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

export const formatSeconds = totalSeconds => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  return [hours, minutes, seconds]
    .map(n => String(n).padStart(2, "0"))
    .join(":")
}

export const formatElapsedTimer = (startedAt, endedAt) => {
  if (!startedAt) return "00:00:00"
  const start = dayjs(startedAt)
  if (!start.isValid()) return "00:00:00"
  const end = endedAt ? dayjs(endedAt) : dayjs()
  if (!end.isValid()) return "00:00:00"
  const totalSeconds = Math.max(0, end.diff(start, "second"))
  return formatSeconds(totalSeconds)
}

/**
 * Map GET /sessions/:id/timelog result into UI timer fields.
 * States: idle | working/running | break | ended
 */
export const mapTimelogResult = (timelog = {}) => {
  const rawState = String(timelog.state || "idle").toLowerCase()
  const sessionStatus = String(timelog.session_status || "").toLowerCase()
  const availableActions = Array.isArray(timelog.available_actions)
    ? timelog.available_actions.map(a => String(a).toLowerCase())
    : []

  const isEnded =
    sessionStatus === "ended" ||
    sessionStatus === "completed" ||
    sessionStatus === "closed" ||
    rawState === "ended"

  let timerStatus = "idle"
  if (isEnded) timerStatus = "ended"
  else if (
    rawState === "break" ||
    rawState === "on_break" ||
    rawState === "paused"
  ) {
    timerStatus = "break"
  } else if (
    rawState === "working" ||
    rawState === "running" ||
    rawState === "active" ||
    rawState === "started"
  ) {
    timerStatus = "running"
  } else if (rawState === "idle") {
    timerStatus = "idle"
  }

  const elapsedSeconds = Number(timelog.totals?.work_seconds) || 0
  const timer = timelog.totals?.work_hhmmss || formatSeconds(elapsedSeconds)

  const nextAction =
    TIMELOG_ACTION_PRIORITY.find(action => availableActions.includes(action)) ||
    null

  return {
    timerStatus,
    elapsedSeconds,
    timer,
    isRunning: timerStatus === "running",
    isOnBreak: timerStatus === "break",
    canToggleTimer: !isEnded && Boolean(nextAction),
    canExit: !isEnded,
    nextTimelogAction: nextAction,
    availableActions,
    sessionStatus: sessionStatus || null,
    collaboratorStatus: timelog.collaborator_status || null,
    serverTimeUtc: timelog.server_time_utc || null,
  }
}

const resolveProjectName = (apiSession, project) =>
  project?.title ||
  project?.name ||
  apiSession?.project?.title ||
  apiSession?.project?.name ||
  apiSession?.targetable?.name ||
  apiSession?.targetable?.title ||
  null

export const formatSessionCityLabel = cityLike => {
  if (!cityLike) return null
  if (typeof cityLike === "object") {
    const name = String(cityLike.name || "").trim()
    if (name.includes(",")) return name

    const cityPart = String(
      cityLike.city || cityLike.name || cityLike.shortName || cityLike.title || "",
    ).trim()
    const state = String(cityLike.state || "").trim()
    if (cityPart && state) return `${cityPart}, ${state}`
    return cityPart || null
  }

  const text = String(cityLike).trim()
  return text || null
}

const resolveCityName = (apiSession, city) =>
  formatSessionCityLabel(city) ||
  formatSessionCityLabel(city?.rawCity) ||
  formatSessionCityLabel(resolveSessionCity(apiSession)) ||
  formatSessionCityLabel(apiSession?.city) ||
  null

export const mapSessionDetail = (apiSession, context = null, timelog = null) => {
  const project =
    context && typeof context === "object" && ("project" in context || "city" in context)
      ? context.project
      : context
  const city =
    context && typeof context === "object" && ("city" in context || "project" in context)
      ? context.city
      : null
  const type = formatSessionType(apiSession?.type)
  const visibility = apiSession?.visibility_type
    ? formatSessionVisibilityLabel(apiSession.visibility_type)
    : "Private"

  const creator = apiSession?.creator || apiSession?.user || null

  const startedBy = creator
    ? {
        name:
          creator.name ||
          creator.full_name ||
          [creator.first_name, creator.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          "—",
        src: getUserAvatarUrl(creator),
      }
    : { name: "—", src: null }

  const timerState = mapTimelogResult(timelog || {})
  const hasStarted =
    timerState.timerStatus !== "idle" || Boolean(apiSession?.started_at)
  const cityName = resolveCityName(apiSession, city)
  const projectName = resolveProjectName(apiSession, project)
  const resolvedProject =
    (project?.id
      ? {
          id: String(project.id),
          name:
            project.name ||
            project.title ||
            projectName ||
            "",
        }
      : null) || resolveSessionProject(apiSession)
  const resolvedCity = city?.id
    ? { id: String(city.id), name: cityName || city.name || city.shortName || "" }
    : resolveSessionCity(apiSession)
  const sessionTarget = String(
    apiSession.session_target || apiSession.source_type || "",
  )
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  const isCitySession =
    sessionTarget === "city" ||
    (Boolean(city?.id || resolvedCity?.id) && !resolvedProject?.id)
  const sourceType = String(
    apiSession.source_type || apiSession.session_target || "",
  )
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  const isTaskSession =
    sourceType === "task" ||
    sourceType === "project_task" ||
    sourceType.endsWith("_task") ||
    sourceType.endsWith("/task")

  const collaborationStatus = String(
    apiSession?.current_user_collaboration_status ||
      timelog?.collaborator_status ||
      "",
  ).toLowerCase()
  const isSessionParticipant = canAccessSessionTimelog(collaborationStatus)
  const hasLeftSession =
    collaborationStatus === "left" || collaborationStatus === "exited"
  const sessionEnded =
    String(apiSession?.status || "").toLowerCase() === "ended" ||
    Boolean(apiSession?.ended_at) ||
    timerState.timerStatus === "ended"
  const visibilityKey = String(apiSession?.visibility_type || "").toLowerCase()
  // City Hub global shadow sessions are open — no invite/add collaborator.
  const canManageCollaborators =
    isSessionParticipant &&
    !hasLeftSession &&
    !sessionEnded &&
    !(isCitySession && visibilityKey === "global")

  const totalWorkingHours = Number(apiSession?.total_working_hours)
  const currentUserWorkingHours = Number(apiSession?.current_user_working_hours)
  const displayTimer = resolveSessionDisplayTimer({
    apiSession,
    timerState,
    sessionEnded,
    hasLeftSession,
    isSessionParticipant,
  })

  return {
    id: apiSession.id,
    title: apiSession.title || apiSession.working_note || "Session Detail",
    taskTitle: isTaskSession ? String(apiSession.title || "").trim() : "",
    type,
    visibility,
    creatorId:
      creator?.id ||
      apiSession?.created_by ||
      apiSession?.creator_id ||
      null,
    startedAt: hasStarted ? formatDateTime(apiSession.started_at) : "—",
    startedBy,
    sessionNote: apiSession.working_note || (isTaskSession ? "—" : apiSession.title) || "—",
    timer: displayTimer.timer,
    elapsedSeconds: displayTimer.elapsedSeconds,
    totalWorkingHours: Number.isFinite(totalWorkingHours) ? totalWorkingHours : null,
    currentUserWorkingHours: Number.isFinite(currentUserWorkingHours)
      ? currentUserWorkingHours
      : null,
    timerStatus:
      !isSessionParticipant || hasLeftSession ? "ended" : timerState.timerStatus,
    isRunning:
      isSessionParticipant && !hasLeftSession ? timerState.isRunning : false,
    isOnBreak:
      isSessionParticipant && !hasLeftSession ? timerState.isOnBreak : false,
    canToggleTimer:
      isSessionParticipant && !hasLeftSession
        ? timerState.canToggleTimer
        : false,
    canExit:
      isSessionParticipant && !hasLeftSession && !sessionEnded
        ? timerState.canExit
        : false,
    canManageCollaborators,
    hasLeftSession,
    isSessionParticipant,
    collaborationStatus: collaborationStatus || null,
    nextTimelogAction:
      isSessionParticipant && !hasLeftSession
        ? timerState.nextTimelogAction
        : null,
    isLive:
      isSessionParticipant && !hasLeftSession
        ? timerState.isRunning || timerState.isOnBreak
        : false,
    startedAtRaw: apiSession.started_at,
    endedAtRaw: apiSession.ended_at,
    status: apiSession.status,
    projectName: resolvedProject?.name || projectName || cityName,
    projectId: resolvedProject?.id || null,
    cityName,
    isCitySession,
    isTaskSession,
    sourceType,
    city: resolvedCity,
  }
}

const memberDisplayName = collaborator => {
  const user = collaborator?.user || {}
  return (
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    user.name ||
    user.email ||
    collaborator.description ||
    "Collaborator"
  )
}

const memberAvatar = collaborator =>
  getUserAvatarUrl(collaborator?.user || collaborator)

export const mapSessionCollaborator = collaborator => {
  const statusKey = String(collaborator.status || "").toLowerCase()
  const currentState = String(collaborator.current_state || "").toLowerCase()
  const isLeft = statusKey === "left" || statusKey === "exited"
  const isPaused = statusKey === "joined" && PAUSED_STATES.has(currentState)
  const isInSession =
    statusKey === "joined" && (ACTIVE_STATES.has(currentState) || !currentState)

  let status =
    SESSION_COLLAB_STATUS_LABEL[statusKey] ||
    capitalize(collaborator.status || "Invited")
  if (isPaused) status = "Paused"
  else if (isInSession) status = "In Session"

  const joinNote =
    collaborator?.working_note ||
    collaborator?.note ||
    collaborator?.description ||
    "—"
  const exitNote = isLeft ? collaborator?.leave_note || "—" : "—"

  return {
    id:
      collaborator.id != null && collaborator.id !== ""
        ? String(collaborator.id)
        : collaborator.id,
    userId: collaborator.user_id || collaborator.user?.id || null,
    name: memberDisplayName(collaborator),
    avatar: memberAvatar(collaborator),
    status,
    statusKey,
    joinNote,
    exitNote,
    note: isLeft ? exitNote : joinNote,
    joinedAt: formatDateTime(
      collaborator.joined_at || collaborator.created_at,
    ),
    logged: formatWorkedHours(collaborator.total_worked_hours),
    exitAt: isLeft
      ? formatDateTime(collaborator.left_at || collaborator.updated_at)
      : "—",
    galleryStatus: isInSession ? "online" : "away",
    raw: collaborator,
  }
}

export const upsertSessionCollaboratorMember = (
  members = [],
  incoming,
  { left = false } = {},
) => {
  if (!incoming || typeof incoming !== "object") {
    return { next: members, added: false }
  }

  const mergedRaw = {
    ...incoming,
    status: incoming.status || (left ? "left" : "joined"),
    ...(left
      ? {
          leave_note:
            incoming.leave_note || incoming.exit_note || incoming.note,
          left_at:
            incoming.left_at || incoming.exited_at || incoming.updated_at,
        }
      : {}),
  }

  const mapped = mapSessionCollaborator(mergedRaw)
  if (mapped.id == null && mapped.userId == null) {
    return { next: members, added: false }
  }

  const index = members.findIndex(
    item =>
      (mapped.id != null && String(item.id) === String(mapped.id)) ||
      (mapped.userId &&
        item.userId &&
        String(item.userId) === String(mapped.userId)),
  )

  if (index === -1) {
    return { next: [mapped, ...members], added: true }
  }

  const existing = members[index]
  const remapped = mapSessionCollaborator({
    ...existing.raw,
    ...mergedRaw,
    user: mergedRaw.user || existing.raw?.user,
  })
  const next = [...members]
  next[index] = {
    ...existing,
    ...remapped,
    name:
      remapped.name && remapped.name !== "Collaborator"
        ? remapped.name
        : existing.name,
    avatar: remapped.avatar || existing.avatar,
  }
  return { next, added: false }
}
